import type { ReactNode } from "react";
import { formatDate, formatTime } from "../format.js";
import type {
  BulkCancelPreview,
  CancellationCreditResolution,
  PaidCancellationAction,
} from "./staff.js";
import { cancellationDecisionRequired } from "./cancellationChoices.js";

export function BulkCancellationDecisions({
  preview,
  busy,
  choices,
  onChoice,
  onChooseAll,
  children,
}: {
  preview: BulkCancelPreview;
  busy: boolean;
  choices: Record<
    number,
    CancellationCreditResolution | PaidCancellationAction
  >;
  onChoice: (
    appointmentId: number,
    resolution: CancellationCreditResolution | PaidCancellationAction,
  ) => void;
  onChooseAll: (resolution: CancellationCreditResolution) => void;
  children?: ReactNode;
}) {
  const blocked = preview.items.filter((item) => !item.can_cancel);
  const required = preview.items.filter(
    (item) =>
      item.can_cancel &&
      item.entitlement?.kind !== "paid_cancellation" &&
      cancellationDecisionRequired(item),
  );
  // Paid lessons never used a credit: their late choice is Issue / Keep
  // payment, kept apart from Return / Keep so the two can't be confused.
  const paidRequired = preview.items.filter(
    (item) =>
      item.can_cancel &&
      item.entitlement?.kind === "paid_cancellation" &&
      item.entitlement.decision_required,
  );
  const keptCredits = required.reduce(
    (sum, item) =>
      choices[item.appointment_id] === "consume"
        ? sum + item.credits_to_restore
        : sum,
    0,
  );
  const creditsPutBack = Math.max(0, preview.credits_to_restore - keptCredits);
  const paidCreditsIssued = preview.items.filter(
    (item) =>
      item.can_cancel &&
      item.entitlement?.kind === "paid_cancellation" &&
      (!item.entitlement.decision_required ||
        choices[item.appointment_id] === "grant_rebooking_credit"),
  ).length;
  const automaticPaidCredits = preview.items.some(
    (item) =>
      item.can_cancel &&
      item.entitlement?.kind === "paid_cancellation" &&
      !item.entitlement.decision_required,
  );
  const needsReview = preview.items.some(
    (item) => item.can_cancel && item.entitlement?.kind === "requires_review",
  );

  return (
    <>
      <h2>What would happen</h2>
      <div className="panel">
        <div className="stats">
          <div className="stat">
            <div className="stat-value tabular">
              {preview.cancellable_count}
            </div>
            <div className="small">lessons cancelled</div>
          </div>
          <div className="stat">
            <div className="stat-value tabular">{creditsPutBack}</div>
            <div className="small">lesson credits put back</div>
          </div>
          <div className="stat">
            <div className="stat-value tabular">{paidCreditsIssued}</div>
            <div className="small">new rebooking credits</div>
          </div>
          <div className="stat">
            <div className="stat-value tabular">
              {preview.already_cancelled_count}
            </div>
            <div className="small">already cancelled</div>
          </div>
          <div className="stat">
            <div className="stat-value tabular">{preview.blocked_count}</div>
            <div className="small">cannot be cancelled</div>
          </div>
        </div>

        {required.length > 0 && (
          <section className="stack" style={{ marginTop: 16 }}>
            <div>
              <h3 style={{ marginBottom: 4 }}>
                Inside 24 hours and paid with a credit
              </h3>
              <p className="small muted" style={{ marginTop: 0 }}>
                Choose what happens to the credit for each late cancellation.
                Nothing is pre-selected.
              </p>
            </div>
            <div className="row" style={{ flexWrap: "wrap" }}>
              <button
                className="quiet small"
                type="button"
                disabled={busy}
                onClick={() => onChooseAll("restore")}
              >
                Return all
              </button>
              <button
                className="quiet small"
                type="button"
                disabled={busy}
                onClick={() => onChooseAll("consume")}
              >
                Keep all used
              </button>
            </div>
            {required.map((item) => (
              <fieldset
                key={item.appointment_id}
                className="slot"
                disabled={busy}
                style={{ minWidth: 0, flexWrap: "wrap" }}
              >
                <legend>
                  {item.booking_start && Number.isFinite(Date.parse(item.booking_start))
                    ? `${formatDate(item.booking_start)} at ${formatTime(item.booking_start)}`
                    : `Lesson #${item.appointment_id}`}
                </legend>
                <label>
                  <input
                    type="radio"
                    name={`credit-resolution-${item.appointment_id}`}
                    value="restore"
                    checked={choices[item.appointment_id] === "restore"}
                    onChange={() => onChoice(item.appointment_id, "restore")}
                  />{" "}
                  Return credit
                </label>
                <label>
                  <input
                    type="radio"
                    name={`credit-resolution-${item.appointment_id}`}
                    value="consume"
                    checked={choices[item.appointment_id] === "consume"}
                    onChange={() => onChoice(item.appointment_id, "consume")}
                  />{" "}
                  Keep credit used
                </label>
              </fieldset>
            ))}
          </section>
        )}

        {paidRequired.length > 0 && (
          <section className="stack" style={{ marginTop: 16 }}>
            <div>
              <h3 style={{ marginBottom: 4 }}>
                Inside 24 hours and already paid for
              </h3>
              <p className="small muted" style={{ marginTop: 0 }}>
                These lessons did not use a credit. Choose whether each payment
                becomes a rebooking credit or is kept as a cancellation charge.
                No refund is made either way. Nothing is pre-selected.
              </p>
            </div>
            {paidRequired.map((item) => (
              <fieldset
                key={item.appointment_id}
                className="slot"
                disabled={busy}
                style={{ minWidth: 0, flexWrap: "wrap" }}
              >
                <legend>
                  {item.booking_start && Number.isFinite(Date.parse(item.booking_start))
                    ? `${formatDate(item.booking_start)} at ${formatTime(item.booking_start)}`
                    : `Lesson #${item.appointment_id}`}
                </legend>
                <label>
                  <input
                    type="radio"
                    name={`paid-resolution-${item.appointment_id}`}
                    value="grant_rebooking_credit"
                    checked={
                      choices[item.appointment_id] === "grant_rebooking_credit"
                    }
                    onChange={() =>
                      onChoice(item.appointment_id, "grant_rebooking_credit")
                    }
                  />{" "}
                  Issue rebooking credit
                </label>
                <label>
                  <input
                    type="radio"
                    name={`paid-resolution-${item.appointment_id}`}
                    value="retain_payment"
                    checked={choices[item.appointment_id] === "retain_payment"}
                    onChange={() =>
                      onChoice(item.appointment_id, "retain_payment")
                    }
                  />{" "}
                  Keep payment as cancellation charge
                </label>
              </fieldset>
            ))}
          </section>
        )}

        {automaticPaidCredits && (
          <p className="small">
            Lessons already paid for with more than 24 hours&apos; notice get
            one rebooking credit each, automatically. No refund is made.
          </p>
        )}
        {needsReview && (
          <p className="small">
            Some lessons have payments that can&apos;t be matched to one whole
            paid lesson. Cancelling sends them to the Action Centre to check
            what the family is owed.
          </p>
        )}

        {blocked.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Lesson</th>
                <th>Why not</th>
              </tr>
            </thead>
            <tbody>
              {blocked.map((item) => (
                <tr key={item.appointment_id}>
                  <td>
                    {item.booking_start && Number.isFinite(Date.parse(item.booking_start))
                      ? formatDate(item.booking_start)
                      : `#${item.appointment_id}`}
                  </td>
                  <td className="small muted">
                    {item.message ?? item.current_status ?? "Blocked"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {children}
      </div>
    </>
  );
}
